/**
 * Shared runner-vs-classic UI toggle decision.
 *
 * ==========================================================================
 *  ⚠  KEEP IN SYNC WITH embed/loader.js  ⚠
 * ==========================================================================
 * The standalone loader (deployed as odoro-lib.js) is served as a plain,
 * un-bundled <script>, so it cannot import this module. It therefore carries
 * its own copy of the decision + fail-safe logic. `pickUiMode()` and
 * `resolveUiMode()` here MUST behave byte-for-byte identically to their twins
 * in embed/loader.js — the decision rule and the fail-safe order are the
 * contract. If you change one, change the other.
 *
 * The back-office dashboard replicates the same resolution + bundle paths in
 * frontend/client/src/js/pages/AppointmentWidget.jsx — keep it aligned too.
 * ==========================================================================
 */

import { getConfigProps } from '@/utils/get-config-props';

export const CONFIG_TIMEOUT_MS = 3000;
export const STORAGE_KEY_PREFIX = 'odoro:uiMode:';
// How long a cached decision stays usable. 5 min matches org2's upstream
// schedulingUiMode cache TTL and the /api/config Cache-Control max-age, so no
// layer holds a decision longer than any other.
//
// Was previously unbounded — savedAt was written but never read, so a persistent
// /config outage pinned a browser to its last decision forever while a browser
// with an empty cache correctly fell back to classic (AB#269554). Must stay
// identical to CACHE_TTL_MS in embed/loader.js and in frontend's
// appointmentWidgetUiMode.js. Exported so tests read the real value.
export const CACHE_TTL_MS = 5 * 60 * 1000;
export const RUNNER_BUNDLE_PATH = '/widget/runner/1.0/runner.js';

// The runner BFF is a distinct hostname from apiUrl (not just a different path
// on the same origin), so we derive it from the env token in apiUrl's host
// rather than a hardcoded default. The scheduling-cloud convention is
// `{app}.{env}.ue1.scheduling.clearwaveinc.cloud`, mirrored by the BFF as
// `workflow-runner-bff.{env}.ue1.scheduling.clearwaveinc.cloud`. There is NO
// prod default on purpose: a dead prod default that only works in prod is what
// silently pointed stage embeds at the (undeployed) prod BFF. If the env can't
// be parsed we fail loud and fall back to classic instead. Explicit
// `runnerApiBaseUrl` on the config always wins over derivation.
export const KNOWN_RUNNER_ENVS = ['dev', 'qa', 'stage', 'prod'];

// Global flag the loader sets before it injects the classic bundle. When the
// classic bundle (index.ts) boots and sees this flag it knows the loader has
// already resolved the UI mode and must NOT fire its own /config request.
export const UIMODE_RESOLVED_FLAG = '__ODORO_UIMODE_RESOLVED__';

export type UiMode = 'classic' | 'runner';

export interface ConfigResponse {
    uiMode?: string;
    fallback?: boolean;
}

export interface RunnerInitArgs {
    container?: string;
    clinicId: string | number;
    apiBaseUrl: string;
    // Params for the session's initial request (authToken,
    // requestedSecurityLevel, PartnerID, ...). Attached only when the page's
    // config carries it, so pages declaring none keep the payload they had
    // before. (The loader's window.odoro branch always produces an object here
    // — empty when the page passed no params. See embed/loader.js.)
    initParams?: Record<string, string>;
}

interface CachedUiMode {
    uiMode: string;
    fallback: boolean;
    savedAt: number;
}

function safeLocalStorageGet (key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

function safeLocalStorageSet (key: string, value: string): void {
    try {
        window.localStorage.setItem(key, value);
    } catch (e) {
        // Quota=0 (Safari private), sandboxed iframe, or storage disabled.
        // Last-known-good simply won't be available next visit; not fatal.
    }
}

function readCachedUiMode (clinicId: string | number): CachedUiMode | null {
    let raw = safeLocalStorageGet(STORAGE_KEY_PREFIX + clinicId);
    if (!raw) return null;
    try {
        let parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.uiMode !== 'string') return null;
        // An entry with no savedAt (or a non-numeric one) predates this check or
        // was hand-written: coercing to 0 makes it read as infinitely stale,
        // which is the safe direction — it falls through to classic rather than
        // being trusted forever.
        //
        // Strictly greater-than, so an entry exactly at the boundary is still
        // honoured. A stale entry returns null (indistinguishable from "no
        // cache") and is deliberately NOT deleted: the next successful /config
        // overwrites it, and keeping it costs nothing.
        let savedAt = typeof parsed.savedAt === 'number' ? parsed.savedAt : 0;
        if (Date.now() - savedAt > CACHE_TTL_MS) return null;
        return parsed;
    } catch (e) {
        // Corrupt entry — ignore; next successful fetch will overwrite.
    }
    return null;
}

function writeCachedUiMode (clinicId: string | number, response: ConfigResponse): void {
    safeLocalStorageSet(STORAGE_KEY_PREFIX + clinicId, JSON.stringify({
        uiMode: response.uiMode,
        fallback: !!response.fallback,
        savedAt: (new Date()).getTime(),
    }));
}

// The Tornado endpoint signals "could not authoritatively decide" via
// `fallback: true`. Treat that as classic, even if it nominally answered.
export function pickUiMode (response: ConfigResponse | CachedUiMode | null | undefined): UiMode {
    if (response && response.uiMode === 'runner' && !response.fallback) {
        return 'runner';
    }
    return 'classic';
}

// Resolves the UI mode for a clinic with the documented fail-safe order:
// 1. Fresh fetch (3s timeout)  →  use & cache
// 2. Fetch failed + cached value newer than CACHE_TTL_MS  →  use cached
// 3. Else (no cache, or cache stale)  →  classic
// Calls `done(uiMode)` exactly once with 'classic' or 'runner'.
export function resolveUiMode (clinicId: string | number, apiUrl: string, done: (uiMode: UiMode) => void): void {
    let settled = false;
    function settle (uiMode: UiMode) {
        if (settled) return;
        settled = true;
        done(uiMode);
    }

    function fallbackToCacheOrClassic () {
        let cached = readCachedUiMode(clinicId);
        settle(cached ? pickUiMode(cached) : 'classic');
    }

    let url = apiUrl + '/config/' + encodeURIComponent(String(clinicId));
    let xhr: XMLHttpRequest | null;
    try {
        xhr = new XMLHttpRequest();
    } catch (e) {
        xhr = null;
    }

    if (!xhr) {
        fallbackToCacheOrClassic();
        return;
    }

    let client = xhr;
    client.open('GET', url, true);
    client.timeout = CONFIG_TIMEOUT_MS;
    client.onreadystatechange = function () {
        if (client.readyState !== 4) return;
        if (client.status >= 200 && client.status < 300) {
            try {
                let response = JSON.parse(client.responseText);
                writeCachedUiMode(clinicId, response);
                settle(pickUiMode(response));
                return;
            } catch (e) {
                // Malformed body — fall through to cache/classic.
            }
        }
        fallbackToCacheOrClassic();
    };
    client.ontimeout = fallbackToCacheOrClassic;
    client.onerror = fallbackToCacheOrClassic;
    try {
        client.send();
    } catch (e) {
        fallbackToCacheOrClassic();
    }
}

// Build the runner bundle URL from an origin. Kept separate from the loader's
// subdomain-based host because the loader is only served from prod CDN hosts,
// whereas the classic bundle can be embedded on stage/non-prod pages and must
// load the runner from the same origin its apiUrl points at.
export function deriveOrigin (url: string): string {
    let a = document.createElement('a');
    a.href = url || '';
    // `a.origin` is unreliable on old IE; rebuild it from protocol + host.
    return a.protocol + '//' + a.host;
}

// Extracts the deploy env (dev|qa|stage|prod) from a scheduling-cloud apiUrl.
// The host is `{app}.{env}.ue1.scheduling.clearwaveinc.cloud`, so the env is
// the label immediately before `ue1`. Returns null for anything that doesn't
// match (e.g. legacy *.odoro.com hosts) so callers can fail loud rather than
// guess. Anchored on `ue1` to avoid matching a stray env-looking label.
export function deriveRunnerEnv (apiUrl: string): string | null {
    let a = document.createElement('a');
    a.href = apiUrl || '';
    let labels = (a.hostname || '').toLowerCase().split('.');
    let ue1Index = labels.indexOf('ue1');
    if (ue1Index > 0 && KNOWN_RUNNER_ENVS.indexOf(labels[ue1Index - 1]) !== -1) {
        return labels[ue1Index - 1];
    }
    return null;
}

// Derives the runner BFF base URL for the env that apiUrl points at, e.g.
// stage apiUrl -> https://workflow-runner-bff.stage.ue1.scheduling.clearwaveinc.cloud/api/workflow
// Returns null when the env can't be parsed (fail-loud signal).
export function deriveRunnerApiBaseUrl (apiUrl: string): string | null {
    let env = deriveRunnerEnv(apiUrl);
    if (!env) return null;
    return 'https://workflow-runner-bff.' + env + '.ue1.scheduling.clearwaveinc.cloud/api/workflow';
}

// Injects the runner bundle and calls WorkflowRunner.init once it loads.
// On ANY failure (bundle 404/blip, missing global, init throw) it calls
// `onFallback` so the page still renders the classic experience — matching
// the loader's "any failure renders classic" guarantee.
export function injectRunner (runnerScriptUrl: string, runnerInitArgs: RunnerInitArgs, onFallback: () => void): void {
    // Tell the runner bundle it's being embedded so it skips its standalone
    // auto-mount path. Must be set BEFORE the script loads.
    window.__WORKFLOW_RUNNER_EMBEDDED__ = true;

    let script = document.createElement('script');
    script.src = runnerScriptUrl;

    let place = document.getElementsByTagName('script')[0];
    if (place && place.parentNode) {
        place.parentNode.insertBefore(script, place);
    } else {
        document.head.appendChild(script);
    }

    script.onload = function () {
        try {
            if (window.WorkflowRunner && typeof window.WorkflowRunner.init === 'function') {
                window.WorkflowRunner.init(runnerInitArgs);
            } else {
                onFallback();
            }
        } catch (e) {
            onFallback();
        }
    };
    script.onerror = function () {
        // Runner bundle failed to load (CDN blip, bad cache, etc.).
        // Render classic so the page still gets a usable scheduling experience.
        onFallback();
    };
}

// A page's primary config, as read from window.OA by the classic bundle.
export interface SelfTogglePrimary {
    clinicId?: string | number | null;
    apiUrl?: string;
    container?: string;
    runnerApiBaseUrl?: string;
    initParams?: Record<string, string>;
}

// Orchestrates the classic bundle's self-toggle. Used by client/src/index.ts
// for pages that embed the classic bundle directly (no odoro-lib.js loader):
//
//   - If the loader already resolved the mode (UIMODE_RESOLVED_FLAG set) or we
//     have no clinicId to ask about, boot classic immediately.
//   - Otherwise resolve the mode; runner → inject the runner bundle + init,
//     classic → boot classic. Any failure/timeout resolves to classic.
//
// `bootClassic` is invoked exactly once, except on the runner path where the
// runner mounts instead (and `bootClassic` only fires if the runner fails).
export function runSelfToggle (primary: SelfTogglePrimary | undefined, bootClassic: () => void): void {
    if ((window as any)[UIMODE_RESOLVED_FLAG]) {
        bootClassic();
        return;
    }

    let clinicId = primary && primary.clinicId;

    // Without a clinicId we can't ask the toggle endpoint — behave as classic,
    // matching the loader's pre-toggle fallback.
    if (!primary || !clinicId) {
        bootClassic();
        return;
    }

    let apiUrl = primary.apiUrl || getConfigProps().apiUrl;
    let runnerScriptUrl = deriveOrigin(apiUrl) + RUNNER_BUNDLE_PATH;
    // Honor the page's container as-is. Intentionally NOT mirroring the loader's
    // `|| '#odoro'` default: classic direct-embeds mount at their own selector
    // (typically `#appoint`), so `#odoro` would be the wrong element here. The
    // loader defaults to `#odoro` because its window.odoro/data-odoro callers
    // use that. Only the decision rule + fail-safe order are kept in lockstep
    // with the loader — orchestration (container, bundle host) differs by design.
    let container = primary.container;
    let explicitRunnerApiBaseUrl = primary.runnerApiBaseUrl;
    // Forwarded for the same reason as in embed/loader.js: classic reads these
    // off the config object itself, the runner only sees the named fields it
    // is handed, and a dropped authToken silently downgrades the session to
    // Public security level (AB#269619).
    let initParams = primary.initParams;

    resolveUiMode(clinicId, apiUrl, function (uiMode) {
        if (uiMode === 'runner') {
            // Explicit config wins; otherwise derive the BFF from apiUrl's env.
            let runnerApiBaseUrl = explicitRunnerApiBaseUrl || deriveRunnerApiBaseUrl(apiUrl);

            // Fail loud rather than silently target prod: without a resolvable
            // BFF the runner's first call would 404, so render classic instead.
            if (!runnerApiBaseUrl) {
                if (window.console && window.console.error) {
                    window.console.error(
                        '[odoro] Could not derive the runner BFF base URL from apiUrl "' + apiUrl +
                        '"; falling back to classic. Set runnerApiBaseUrl on the widget config to enable the runner UI.',
                    );
                }
                bootClassic();
                return;
            }

            let runnerInitArgs: RunnerInitArgs = {
                container,
                clinicId: clinicId as string | number,
                apiBaseUrl: runnerApiBaseUrl,
            };

            // Attach only when the page's config actually carries params, so
            // direct-embed pages that declare none keep exactly the payload
            // they had before.
            if (initParams && typeof initParams === 'object') {
                runnerInitArgs.initParams = initParams;
            }

            injectRunner(runnerScriptUrl, runnerInitArgs, bootClassic);
        } else {
            bootClassic();
        }
    });
}
